import Phaser from "phaser";
import { goTo } from "../ui/sceneTransition";
import { GAME_WIDTH } from "../config";
import { save } from "../systems/Save";
import { SKILL_NODES, SkillNode, nodeIsAvailable, nodeRank } from "../systems/SkillTree";
import { THEME, panel } from "../ui/theme";
import {
  addCastleBackdrop,
  addContentPanel,
  addMenuBackdrop,
  addMenuHeader,
  addQuitButton,
  addPageFooter,
  beginSceneLifecycle,
  menuButton,
} from "../ui/menuUi";

type ReturnTarget = "menu" | "game";

interface TrunkDefinition {
  key: SkillNode["trunk"];
  title: string;
  subtitle: string;
  color: number;
}

interface NodeView {
  container: Phaser.GameObjects.Container;
  node: SkillNode;
  background: Phaser.GameObjects.NineSlice;
  pips: Phaser.GameObjects.Arc[];
  stateLabel: Phaser.GameObjects.Text;
  color: number;
}

const TRUNKS: TrunkDefinition[] = [
  { key: "crown", title: "CROWN", subtitle: "Economy & tribute", color: THEME.hex.gold },
  { key: "forge", title: "FORGE", subtitle: "Towers & craft", color: THEME.hex.forge },
  { key: "banner", title: "BANNER", subtitle: "Commander abilities", color: THEME.hex.mana },
];

const CONTENT = { x: 24, y: 96, width: 1232, height: 558 };
const COLUMN_TOP = 164;
const NODE_FIRST_Y = 226;
const NODE_STEP = 78;
const NODE_WIDTH = 370;
const NODE_HEIGHT = 68;

export class SkillTreeScene extends Phaser.Scene {
  private headerText!: Phaser.GameObjects.Text;
  private nodeGfx: NodeView[] = [];
  private returnTo: ReturnTarget = "menu";

  constructor() { super("SkillTreeScene"); }

  init(data: { returnTo?: ReturnTarget }): void {
    this.returnTo = data?.returnTo === "game" ? "game" : "menu";
  }

  create(): void {
    const lifecycle = beginSceneLifecycle(this);

    // Phaser reuses Scene instances. Never retain views destroyed by a prior
    // shutdown; refresh() must only inspect objects created in this run.
    this.nodeGfx = [];
    save.load();

    addMenuBackdrop(this, { darkness: 0.8, parallax: true, texture: "bg-skilltree" });
    addMenuHeader(this, {
      title: "SKILL TREE",
      subtitle: "Shape the commander's doctrine",
    });
    addQuitButton(this);
    addContentPanel(this, CONTENT, "iron", 0.97);
    addCastleBackdrop(this, CONTENT, { veil: "dark" });

    menuButton(this, 106, 47, 148, 44, "< BACK", {
      name: "skill-tree-back",
      display: true,
      fontSize: 15,
      onClick: () => this.exit(),
    });
    menuButton(this, GAME_WIDTH - 184, 47, 164, 44, "REFUND ALL", {
      name: "skill-tree-refund",
      display: true,
      fontSize: 14,
      color: THEME.colors.parchmentDim,
      onClick: () => this.refundAll(),
    });

    this.headerText = this.add.text(GAME_WIDTH / 2, 118, "", {
      fontFamily: THEME.fonts.display,
      fontSize: "17px",
      color: THEME.colors.parchment,
    }).setOrigin(0.5);

    const divider = this.add.graphics();
    divider.lineStyle(1, THEME.hex.goldDim, 0.55);
    divider.lineBetween(CONTENT.x + 18, 144, CONTENT.x + CONTENT.width - 18, 144);

    const innerX = CONTENT.x + 22;
    const innerWidth = CONTENT.width - 44;
    const columnGap = 14;
    const columnWidth = (innerWidth - columnGap * 2) / 3;

    TRUNKS.forEach((trunk, index) => {
      const columnLeft = innerX + index * (columnWidth + columnGap);
      const centerX = columnLeft + columnWidth / 2;

      panel(this, columnLeft, 154, columnWidth, CONTENT.height - 72, "iron-thin").setAlpha(0.42);

      this.add.text(centerX, COLUMN_TOP, trunk.title, {
        fontFamily: THEME.fonts.display,
        fontSize: "22px",
        color: this.colorString(trunk.color),
        stroke: THEME.colors.ironDeep,
        strokeThickness: 3,
      }).setOrigin(0.5, 0);
      this.add.text(centerX, COLUMN_TOP + 26, trunk.subtitle, {
        fontFamily: THEME.fonts.body,
        fontSize: "13px",
        color: THEME.colors.parchmentDim,
      }).setOrigin(0.5, 0);

      const connection = this.add.graphics();
      // 1. Draw shadow base
      connection.lineStyle(6, THEME.hex.ironDeep, 0.85);
      connection.lineBetween(centerX, NODE_FIRST_Y, centerX, NODE_FIRST_Y + NODE_STEP * 4);
      // 2. Draw colored rope line
      connection.lineStyle(3.5, trunk.color, 0.72);
      connection.lineBetween(centerX, NODE_FIRST_Y, centerX, NODE_FIRST_Y + NODE_STEP * 4);
      // 3. Draw braided dashes
      connection.lineStyle(1.5, THEME.hex.goldDim, 0.55);
      const totalLen = NODE_STEP * 4;
      const steps = Math.floor(totalLen / 6);
      for (let i = 0; i < steps; i += 2) {
        const sy = NODE_FIRST_Y + i * 6;
        connection.lineBetween(centerX, sy, centerX, sy + 3);
      }

      const nodes = SKILL_NODES
        .filter(node => node.trunk === trunk.key)
        .sort((a, b) => a.row - b.row);

      nodes.forEach(node => {
        const y = NODE_FIRST_Y + node.row * NODE_STEP;
        this.nodeGfx.push(this.makeNode(node, centerX, y, trunk.color));
      });
    });

    lifecycle.key("keydown-ESC", () => this.exit());
    this.refresh();
    addPageFooter(this, "Select a doctrine to spend one skill point  |  ESC returns");
  }

  private exit(): void {
    if (this.returnTo === "game") {
      this.scene.resume("GameScene");
      this.scene.resume("UIScene");
      this.scene.stop();
      return;
    }
    goTo(this, "MenuScene");
  }

  private refundAll(): void {
    const spent = Object.values(save.get().spentSkillPoints)
      .reduce((sum, rank) => sum + rank, 0);
    if (spent <= 0) {
      this.flash("No spent skill points to refund.", THEME.colors.parchmentDim);
      return;
    }

    save.refundAllSkillPoints();
    this.refresh();
    this.flash(`Refunded ${spent} skill point${spent === 1 ? "" : "s"}.`, THEME.colors.gold);
  }

  private makeNode(node: SkillNode, x: number, y: number, color: number): NodeView {
    const container = this.add.container(x, y);
    const background = panel(
      this,
      -NODE_WIDTH / 2,
      -NODE_HEIGHT / 2,
      NODE_WIDTH,
      NODE_HEIGHT,
      "iron",
    );
    background.setInteractive({ useHandCursor: true });

    // Icon slot background/frame
    const iconBg = this.add.circle(-NODE_WIDTH / 2 + 28, 0, 22, THEME.hex.ironDeep, 1)
      .setStrokeStyle(2, color, 0.85);

    // Skill icon image sprite
    const crest = this.add.image(-NODE_WIDTH / 2 + 28, 0, `skill-${node.id}`)
      .setDisplaySize(38, 38);

    // Mask to clip the square corners of the hand-painted icons
    const maskShape = this.make.graphics({}, false);
    maskShape.fillStyle(0xffffff, 1);
    maskShape.fillCircle(x - NODE_WIDTH / 2 + 28, y, 17);
    const mask = maskShape.createGeometryMask();
    crest.setMask(mask);

    const name = this.add.text(-NODE_WIDTH / 2 + 58, -NODE_HEIGHT / 2 + 8, node.name, {
      fontFamily: THEME.fonts.display,
      fontSize: "15px",
      color: THEME.colors.parchment,
    });
    const description = this.add.text(-NODE_WIDTH / 2 + 58, -NODE_HEIGHT / 2 + 29, node.description, {
      fontFamily: THEME.fonts.body,
      fontSize: "12px",
      color: THEME.colors.parchmentDim,
      wordWrap: { width: NODE_WIDTH - 120 },
      lineSpacing: -2,
    });
    const stateLabel = this.add.text(NODE_WIDTH / 2 - 12, -NODE_HEIGHT / 2 + 8, "", {
      fontFamily: THEME.fonts.display,
      fontSize: "11px",
      color: THEME.colors.goldDim,
    }).setOrigin(1, 0);

    const pips: Phaser.GameObjects.Arc[] = [];
    for (let index = 0; index < node.maxRank; index++) {
      const pip = this.add.circle(
        NODE_WIDTH / 2 - 14 - index * 14,
        NODE_HEIGHT / 2 - 13,
        5,
        THEME.hex.lock,
      ).setStrokeStyle(1, THEME.hex.ironEdge, 0.9);
      pips.push(pip);
    }

    container.add([background, iconBg, crest, name, description, stateLabel, ...pips]);
    background.on("pointerover", () => {
      if (nodeIsAvailable(node) && nodeRank(node.id) < node.maxRank) background.setTint(0xffeec0);
    });
    background.on("pointerout", () => this.applyNodeVisual({
      container, node, background, pips, stateLabel, color,
    }));
    background.on("pointerdown", () => this.tryBuy(node));

    return { container, node, background, pips, stateLabel, color };
  }

  private tryBuy(node: SkillNode): void {
    if (!nodeIsAvailable(node)) {
      this.flash("Locked: purchase the prerequisite first.", THEME.colors.blood);
      return;
    }
    if (nodeRank(node.id) >= node.maxRank) {
      this.flash("This doctrine is already mastered.", THEME.colors.parchmentDim);
      return;
    }
    if (save.get().skillPoints <= 0) {
      this.flash("No skill points available.", THEME.colors.blood);
      return;
    }

    save.spendSkillPoint(node.id, node.maxRank);
    this.refresh();
  }

  private refresh(): void {
    const data = save.get();
    this.headerText.setText(
      `COMMANDER LEVEL ${data.playerLevel}    |    ${data.skillPoints} SKILL POINT${data.skillPoints === 1 ? "" : "S"}`,
    );
    this.nodeGfx.forEach(view => this.applyNodeVisual(view));
  }

  private applyNodeVisual(view: NodeView): void {
    if (!view.container.active || !view.background.active) return;

    const rank = nodeRank(view.node.id);
    const available = nodeIsAvailable(view.node);
    const maxed = rank >= view.node.maxRank;

    view.background.clearTint();
    if (maxed) view.background.setTint(view.color);
    view.container.setAlpha(available ? 1 : 0.48);
    view.stateLabel
      .setText(maxed ? "MASTERED" : available ? `${rank}/${view.node.maxRank}` : "LOCKED")
      .setColor(maxed ? THEME.colors.gold : available ? THEME.colors.parchmentDim : THEME.colors.lock);
    view.pips.forEach((pip, index) => {
      pip.setFillStyle(index < rank ? view.color : THEME.hex.lock, index < rank ? 1 : 0.7);
    });
  }

  private flash(message: string, color: string): void {
    const text = this.add.text(0, 0, message, {
      fontFamily: THEME.fonts.display,
      fontSize: "15px",
      color,
    }).setOrigin(0.5);
    const width = Math.max(260, text.width + 34);
    const toast = this.add.container(GAME_WIDTH / 2, 120).setDepth(THEME.depths.toast);
    const background = panel(this, -width / 2, -20, width, 40, "iron");
    toast.add([background, text]);
    toast.setAlpha(0).setY(112);
    this.tweens.add({
      targets: toast,
      alpha: 1,
      y: 120,
      duration: 140,
      ease: "Sine.easeOut",
      onComplete: () => this.tweens.add({
        targets: toast,
        alpha: 0,
        y: 106,
        delay: 1_200,
        duration: 320,
        onComplete: () => toast.destroy(),
      }),
    });
  }

  private colorString(color: number): string {
    return `#${color.toString(16).padStart(6, "0")}`;
  }
}
